'use-client';
import React from 'react';
import type {
  IClassicPaginationProps,
  IPaginationFormProps,
  IPaginationStripProps,
} from '../Pagination.types';
import semanticClassNames from '../Pagination.semanticClassNames';
import { NavButton } from './NavButton';
import { First, Last, Next, Previous } from './assets';
import { st, classes } from './style/Pagination.st.css';
import { testIds } from './constants';
import { useFocusNavButtons } from './hooks/useFocusNavButtons';
import PageForm from './skinComps/PaginationForm/PageForm';
import PageStrip from './skinComps/PaginationStrip/PageStrip';
import {
  formatClassNames,
  getAccessibilityAttributes,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';

const Pagination: React.FC<IClassicPaginationProps> = props => {
  const {
    id,
    customClassNames = [],
    isDisabled,
    forceNextButtonEnabled,
    forcePreviousButtonEnabled,
    onChange,
    paginationDirection,
    currentPage,
    totalPages,
    showFirstLastNavButtons,
    firstText,
    firstAriaLabel,
    previousText,
    previousAriaLabel,
    paginationMode,
    nextText,
    nextAriaLabel,
    lastText,
    lastAriaLabel,
    navigationType,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    isResponsiveHeight,
    onNextClick,
    onPrevClick,
    className,
    previousPageUrl,
    nextPageUrl,
    a11y,
    ariaAttributes,
    ariaLabelTranslations,
    showClassicPaginationAsList,
    lang,
  } = props;

  const nextButtonRef = React.useRef<HTMLAnchorElement>(null);
  const previousButtonRef = React.useRef<HTMLAnchorElement>(null);
  const nextButtonContainerRef = React.useRef<HTMLDivElement>(null);

  const {
    isPreviousButtonEnabled,
    isNextButtonEnabled,
    setShouldFocusNavigationButtons,
  } = useFocusNavButtons({
    forcePreviousButtonEnabled,
    forceNextButtonEnabled,
    isDisabled,
    currentPage,
    totalPages,
    nextButtonRef,
    previousButtonRef,
  });

  const a11yProps = getAccessibilityAttributes({
    ariaAttributes: { label: a11y?.label, ...ariaAttributes },
  });

  const notifyPageChanged = (page: number) => {
    setShouldFocusNavigationButtons(true);
    onChange({
      type: 'change',
      target: { value: { currentPage: page } },
    });
  };

  const pageSelectorChangeHandler = (page: number) => notifyPageChanged(page);

  const navButtonChangeHandler = (page: number) => () =>
    notifyPageChanged(page);

  const nextButtonChangeHandler = () => {
    onNextClick?.({ type: 'onNextClick' });
    navButtonChangeHandler(currentPage + 1)();
  };

  const prevButtonChangeHandler = () => {
    onPrevClick?.({ type: 'onPrevClick' });
    navButtonChangeHandler(currentPage - 1)();
  };

  // Adjustments for the component height on mobile with PageStrip - required for pixel-perfect match w/legacy
  const [minHeight, setMinHeight] = React.useState<number>(0);
  React.useEffect(() => {
    const nextButtonContainerElem = nextButtonContainerRef.current;
    if (
      paginationMode === 'input' ||
      !isResponsiveHeight ||
      nextButtonContainerElem === null
    ) {
      return;
    }

    setMinHeight(nextButtonContainerElem.offsetHeight); // Web standards will round this up!
  }, [isResponsiveHeight, paginationMode]);

  /* 
    The 'Pages' mode dynamic template breaks when it is wrapped inside an element.
    Therefore  'page' paginationMode isn't part of the Accessibility Blitz.
  */
  return showClassicPaginationAsList && paginationMode !== 'pages' ? (
    <ul
      id={id}
      dir={paginationDirection}
      className={st(
        classes.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      {...getDataAttributes(props)}
      data-testid={testIds.root}
      aria-label={
        a11yProps.ariaAttributes['aria-label'] ||
        ariaLabelTranslations.paginationLabel
      }
      style={minHeight > 0 ? { minHeight: `${minHeight}px` } : {}}
      {...(!isDisabled && {
        onClick,
        onDoubleClick: onDblClick,
        onMouseEnter,
        onMouseLeave,
      })}
      lang={lang}
    >
      {showFirstLastNavButtons && (
        <li className={classes.navButtonsContainer}>
          <NavButton
            testId={testIds.navButtonFirst}
            className={st(
              classes.navButton,
              { navigationType },
              showFirstLastNavButtons ? classes.navButtonGap : undefined,
              formatClassNames(semanticClassNames.navigationButton),
            )}
            ariaLabel={
              firstAriaLabel ?? firstText ?? ariaLabelTranslations.firstPage
            }
            onChange={navButtonChangeHandler(1)}
            isDisabled={isDisabled || currentPage <= 1}
            showClassicPaginationAsList={showClassicPaginationAsList}
          >
            {navigationType === 'arrows' ? <First /> : <span>{firstText}</span>}
          </NavButton>
        </li>
      )}
      <li className={classes.navButtonsContainer}>
        <NavButton
          ref={previousButtonRef}
          testId={testIds.navButtonPrev}
          className={st(
            classes.navButton,
            { navigationType },
            formatClassNames(semanticClassNames.navigationButton),
          )}
          url={previousPageUrl}
          ariaLabel={
            previousAriaLabel ??
            previousText ??
            ariaLabelTranslations.previousPage
          }
          onChange={prevButtonChangeHandler}
          isDisabled={!isPreviousButtonEnabled}
          showClassicPaginationAsList={showClassicPaginationAsList}
        >
          {navigationType === 'arrows' ? (
            <Previous />
          ) : (
            <span>{previousText}</span>
          )}
        </NavButton>
      </li>
      <li
        aria-live="polite"
        aria-atomic
        className={
          paginationMode === 'input' ? classes.pageFormContainer : undefined
        }
      >
        {props.paginationMode === 'input' ? (
          <PageForm
            key={props.currentPage}
            {...(props as IPaginationFormProps)}
            onChange={pageSelectorChangeHandler}
            ariaLabelTranslations={ariaLabelTranslations}
          />
        ) : (
          <PageStrip
            key={props.currentPage}
            {...(props as IPaginationStripProps)}
            onChange={pageSelectorChangeHandler}
            ariaLabelTranslations={ariaLabelTranslations}
          />
        )}
      </li>
      <li>
        <div
          className={classes.navButtonsContainer}
          ref={nextButtonContainerRef}
        >
          <NavButton
            ref={nextButtonRef}
            testId={testIds.navButtonNext}
            className={st(
              classes.navButton,
              { navigationType },
              showFirstLastNavButtons ? classes.navButtonGap : undefined,
              formatClassNames(semanticClassNames.navigationButton),
            )}
            url={nextPageUrl}
            ariaLabel={
              nextAriaLabel ?? nextText ?? ariaLabelTranslations.nextPage
            }
            onChange={nextButtonChangeHandler}
            isDisabled={!isNextButtonEnabled}
            showClassicPaginationAsList={showClassicPaginationAsList}
          >
            {navigationType === 'arrows' ? <Next /> : <span>{nextText}</span>}
          </NavButton>
        </div>
      </li>
      {showFirstLastNavButtons && (
        <li className={classes.navButtonsContainer}>
          <NavButton
            testId={testIds.navButtonLast}
            className={st(
              classes.navButton,
              { navigationType },
              formatClassNames(semanticClassNames.navigationButton),
            )}
            ariaLabel={
              lastAriaLabel ?? lastText ?? ariaLabelTranslations.lastPage
            }
            onChange={navButtonChangeHandler(totalPages)}
            isDisabled={isDisabled || currentPage >= totalPages}
            showClassicPaginationAsList={showClassicPaginationAsList}
          >
            {navigationType === 'arrows' ? <Last /> : <span>{lastText}</span>}
          </NavButton>
        </li>
      )}
    </ul>
  ) : (
    <nav
      id={id}
      dir={paginationDirection}
      className={st(
        classes.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      {...getDataAttributes(props)}
      data-testid={testIds.root}
      aria-label={a11yProps.ariaAttributes['aria-label'] || 'Pagination'}
      style={minHeight > 0 ? { minHeight: `${minHeight}px` } : {}}
      {...(!isDisabled && {
        onClick,
        onDoubleClick: onDblClick,
        onMouseEnter,
        onMouseLeave,
      })}
      lang={lang}
    >
      <div className={classes.navButtonsContainer}>
        {showFirstLastNavButtons && (
          <NavButton
            testId={testIds.navButtonFirst}
            className={st(
              classes.navButton,
              { navigationType },
              showFirstLastNavButtons ? classes.navButtonGap : undefined,
              formatClassNames(semanticClassNames.navigationButton),
            )}
            ariaLabel={firstAriaLabel || firstText || 'First Page'}
            onChange={navButtonChangeHandler(1)}
            isDisabled={isDisabled || currentPage <= 1}
          >
            {navigationType === 'arrows' ? <First /> : <span>{firstText}</span>}
          </NavButton>
        )}
        <NavButton
          ref={previousButtonRef}
          testId={testIds.navButtonPrev}
          className={st(
            classes.navButton,
            { navigationType },
            formatClassNames(semanticClassNames.navigationButton),
          )}
          url={previousPageUrl}
          ariaLabel={previousAriaLabel || previousText || 'Previous Page'}
          onChange={prevButtonChangeHandler}
          isDisabled={!isPreviousButtonEnabled}
        >
          {navigationType === 'arrows' ? (
            <Previous />
          ) : (
            <span>{previousText}</span>
          )}
        </NavButton>
      </div>
      {props.paginationMode === 'input' ? (
        <PageForm
          key={props.currentPage}
          {...(props as IPaginationFormProps)}
          onChange={pageSelectorChangeHandler}
        />
      ) : (
        <PageStrip
          key={props.currentPage}
          {...(props as IPaginationStripProps)}
          onChange={pageSelectorChangeHandler}
        />
      )}
      <div className={classes.navButtonsContainer} ref={nextButtonContainerRef}>
        <NavButton
          ref={nextButtonRef}
          testId={testIds.navButtonNext}
          className={st(
            classes.navButton,
            { navigationType },
            showFirstLastNavButtons ? classes.navButtonGap : undefined,
            formatClassNames(semanticClassNames.navigationButton),
          )}
          url={nextPageUrl}
          ariaLabel={nextAriaLabel || nextText || 'Next Page'}
          onChange={nextButtonChangeHandler}
          isDisabled={!isNextButtonEnabled}
        >
          {navigationType === 'arrows' ? <Next /> : <span>{nextText}</span>}
        </NavButton>
        {showFirstLastNavButtons && (
          <NavButton
            testId={testIds.navButtonLast}
            className={st(
              classes.navButton,
              { navigationType },
              formatClassNames(semanticClassNames.navigationButton),
            )}
            ariaLabel={lastAriaLabel || lastText || 'Last Page'}
            onChange={navButtonChangeHandler(totalPages)}
            isDisabled={isDisabled || currentPage >= totalPages}
          >
            {navigationType === 'arrows' ? <Last /> : <span>{lastText}</span>}
          </NavButton>
        )}
      </div>
    </nav>
  );
};

export default Pagination;
